import { DollarAmount } from "../common/DollarAmount";
import { RyeInput } from "../rye/RyeInput";
import { RyeMonthPicker } from "../rye/RyeMonthPicker";
import { RyeIcon } from "../rye/RyeIcon";
import { RyeTooltip } from "../rye/RyeTooltip";
import { buildClasses } from "../utils/buildClasses";
import { BudgetLineItem, BreadBudget, FirestoreDocCategory } from "breadcommon";
import { useContext, useState } from "react";
import { Dayjs } from "dayjs";
import { UserContext } from "../firebaseio/UserContext";
import { CategoryGroupsContext } from "../firebaseio/CategoryGroupsContext";
import { TransactionsContext } from "../firebaseio/TransactionsContext";
import { useExchangeRates } from "../firebaseio/ExchangeRatesContext";
import { useGetGroupedCategoriesForBudget } from "./useGetGroupedCategoriesForBudget";
import { firestoreUpdateBudgetLineItem } from "../firebaseio/firestoreIo";
import { CategoryTypeIcon } from "../categories_screen/CategoryTypeIcon";
import { getSignedBudgetLineItemAmount } from "./budgetUtils";

export function BudgetEditor(props: {
  budget: BreadBudget | null;
  isEditing: boolean;
  showHistorical: boolean;
}) {
  const groupedCategories = useGetGroupedCategoriesForBudget();
  const [groupIsExpanded, setGroupIsExpanded] = useState<Map<string, boolean>>(
    new Map(),
  );

  function anyGroupsAreExpanded(): boolean {
    // Check all available groups, not just those in the map
    for (const [groupId] of Array.from(groupedCategories.entries())) {
      const isExpanded = groupIsExpanded.get(groupId) ?? true;
      if (isExpanded) return true;
    }
    return false;
  }

  function expandOrCollapseAllGroups() {
    const shouldCollapse = anyGroupsAreExpanded();
    setGroupIsExpanded((prevState) => {
      const newState = new Map(prevState);
      // Operate on all available groups, not just those in the map
      Array.from(groupedCategories.keys()).forEach((groupId) =>
        newState.set(groupId, !shouldCollapse),
      );
      return newState;
    });
  }

  function setIndividualGroupIsExpanded(
    groupId: string,
    isExpanded: boolean,
  ): void {
    setGroupIsExpanded((prevState) => {
      const newState = new Map(prevState);
      newState.set(groupId, isExpanded);
      return newState;
    });
  }

  if (!props.budget) {
    return (
      <div
        className={buildClasses(
          "w-full",
          "h-full",
          "flex",
          "items-center",
          "justify-center",
          "text-lg",
          "text-on-surface-500",
        )}
      >
        Create a budget to get started.
      </div>
    );
  }
  return (
    <div className={buildClasses("flex", "items-stretch")}>
      <LeftPanel
        budget={props.budget}
        showHistorical={props.showHistorical}
        isEditing={props.isEditing}
        groupIsExpanded={groupIsExpanded}
        anyGroupsAreExpanded={anyGroupsAreExpanded()}
        expandOrCollapseAllGroups={expandOrCollapseAllGroups}
        setIndividualGroupIsExpanded={setIndividualGroupIsExpanded}
      />
      <div
        className={buildClasses(
          "flex",
          "transition-all",
          "duration-300",
          "ease-in-out",
          "overflow-hidden",
          {
            if: props.showHistorical,
            then: "max-w-[600px] opacity-100",
            else: "max-w-0 opacity-0",
          },
        )}
      >
        <PanelDivider />
        <RightPanel
          key={props.budget.id}
          budget={props.budget}
          groupIsExpanded={groupIsExpanded}
          setIndividualGroupIsExpanded={setIndividualGroupIsExpanded}
        />
      </div>
    </div>
  );
}

function PanelDivider() {
  return (
    <div className={buildClasses("w-px", "flex-shrink-0", "bg-surface-600")} />
  );
}

function LeftPanel({
  budget,
  showHistorical,
  isEditing,
  groupIsExpanded,
  anyGroupsAreExpanded,
  expandOrCollapseAllGroups,
  setIndividualGroupIsExpanded,
}: {
  budget: BreadBudget;
  showHistorical: boolean;
  isEditing: boolean;
  groupIsExpanded: Map<string, boolean>;
  anyGroupsAreExpanded: boolean;
  expandOrCollapseAllGroups: () => void;
  setIndividualGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
}) {
  const categoryGroups = useContext(CategoryGroupsContext);
  const groupedCategories = useGetGroupedCategoriesForBudget();

  // Create rows for each category group and its categories
  const rows: JSX.Element[] = [];

  Array.from(groupedCategories.entries()).forEach(([groupId, categories]) => {
    const group = categoryGroups.get(groupId);
    if (!group) return;

    // Calculate group totals
    let groupTotal = 0;
    let groupMonthlyAvg = 0;
    Array.from(categories.values()).forEach((category) => {
      const lineItem = budget.line_items.get(category.id);
      if (lineItem && lineItem.formula_is_valid) {
        groupTotal += getSignedBudgetLineItemAmount(lineItem, category);
      }
    });

    // Calculate monthly average for the group
    const numMonths = Math.ceil(
      budget.endDateTimestampSecs.diff(
        budget.beginDateTimestampSecs,
        "month",
        true,
      ),
    );
    groupMonthlyAvg = numMonths > 0 ? groupTotal / numMonths : 0;

    const isExpanded = groupIsExpanded.get(groupId) ?? true;

    // Add group with smooth transitions
    rows.push(
      <EditingTableGroup
        key={`group-${groupId}`}
        groupId={groupId}
        group={group}
        categories={categories}
        groupTotal={groupTotal}
        groupMonthlyAvg={groupMonthlyAvg}
        isExpanded={isExpanded}
        setGroupIsExpanded={setIndividualGroupIsExpanded}
        budget={budget}
        isEditing={isEditing}
      />,
    );
  });

  return (
    <div
      className={buildClasses(
        "grow",
        "transition-[padding-right]",
        "duration-300",
        "ease-in-out",
        { if: showHistorical, then: "pr-8", else: "pr-0" },
      )}
    >
      {/* Matches the historical panel's title row, so the two tables' rows
      line up, and opens and closes with it. */}
      <div
        aria-hidden
        className={buildClasses(
          "transition-[height]",
          "duration-300",
          "ease-in-out",
          { if: showHistorical, then: TITLE_ROW_HEIGHT, else: "h-0" },
        )}
      />
      <EditingTableHeaderRow
        anyGroupsAreExpanded={anyGroupsAreExpanded}
        expandOrCollapseAllGroups={expandOrCollapseAllGroups}
        isEditing={isEditing}
      />
      {rows}
    </div>
  );
}

function RightPanel({
  budget,
  groupIsExpanded,
  setIndividualGroupIsExpanded,
}: {
  budget: BreadBudget;
  groupIsExpanded: Map<string, boolean>;
  setIndividualGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
}) {
  const transactions = useContext(TransactionsContext);
  const { convertTransactionsToSpecificCurrency } = useExchangeRates();
  const categoryGroups = useContext(CategoryGroupsContext);
  const groupedCategories = useGetGroupedCategoriesForBudget();

  // Local state for historical range pickers (initialize from budget)
  const [histStart, setHistStart] = useState<Dayjs | null>(
    budget.beginDateTimestampSecs.subtract(1, "year"),
  );
  const [histEnd, setHistEnd] = useState<Dayjs | null>(
    budget.endDateTimestampSecs.subtract(1, "year"),
  );

  function onHistStartChange(newStart: Dayjs | null) {
    if (!newStart) return;
    const startOfMonth = newStart.startOf("month");
    setHistStart(startOfMonth);
    if (histEnd && startOfMonth.isAfter(histEnd)) {
      setHistEnd(newStart.endOf("month"));
    }
  }

  function onHistEndChange(newEnd: Dayjs | null) {
    if (!newEnd) return;
    const endOfMonth = newEnd.endOf("month");
    setHistEnd(endOfMonth);
    if (histStart && endOfMonth.isBefore(histStart)) {
      setHistStart(newEnd.startOf("month"));
    }
  }

  const start = histStart || budget.beginDateTimestampSecs;
  const end = histEnd || budget.endDateTimestampSecs;
  const numMonths = Math.ceil(end.diff(start, "month", true));

  // Calculate historical spending per category for the selected range
  const historicalSpending = new Map<string, number>();
  const convertedTransactions = convertTransactionsToSpecificCurrency(
    Array.from(transactions.values()),
    budget.currency,
  );
  convertedTransactions
    .filter((transaction) => {
      const transactionDate = transaction.date;
      return transactionDate.isAfter(start) && transactionDate.isBefore(end);
    })
    .forEach((transaction) => {
      const categoryId = transaction.categoryId;
      if (categoryId) {
        const currentAmount = historicalSpending.get(categoryId) || 0;
        historicalSpending.set(categoryId, currentAmount + transaction.amount);
      }
    });

  // Create rows for each category group and its categories
  const rows: JSX.Element[] = [];

  Array.from(groupedCategories.entries()).forEach(([groupId, categories]) => {
    const group = categoryGroups.get(groupId);
    if (!group) return;

    // Calculate group totals
    let groupTotal = 0;
    Array.from(categories.values()).forEach((category) => {
      groupTotal += historicalSpending.get(category.id) || 0;
    });

    const isExpanded = groupIsExpanded.get(groupId) ?? true;

    // Add group with smooth transitions
    rows.push(
      <HistoricalTableGroup
        key={`hist-group-${groupId}`}
        groupId={groupId}
        group={group}
        categories={categories}
        groupTotal={groupTotal}
        numMonths={numMonths}
        isExpanded={isExpanded}
        setGroupIsExpanded={setIndividualGroupIsExpanded}
        historicalSpending={historicalSpending}
        currency={budget.currency}
      />,
    );
  });

  return (
    <div className={buildClasses("shrink-0", "pl-8")}>
      <TitleRow>
        <div
          className={buildClasses("w-full", "flex", "items-center", "gap-4")}
        >
          <div className={buildClasses("text-lg", "font-medium")}>
            Historical data
          </div>
          <div
            className={buildClasses("flex", "items-center", "gap-2", "ml-auto")}
          >
            <RyeMonthPicker
              selectedDate={start}
              onDateChange={onHistStartChange}
              size="sm"
              width="xs"
            />
            through
            <RyeMonthPicker
              selectedDate={end}
              onDateChange={onHistEndChange}
              size="sm"
              width="xs"
              align="right"
            />
          </div>
        </div>
      </TitleRow>
      <HistoricalTableHeaderRow />
      {rows}
    </div>
  );
}

// The historical title row's height plus its gap below (h-11 and mb-10), for
// the space that lines the budget table up with it.
const TITLE_ROW_HEIGHT = "h-[5.25rem]";

function TitleRow(props: { children?: React.ReactNode }) {
  return (
    <div
      className={buildClasses(
        "w-full",
        "h-11",
        "flex",
        "items-center",
        "mb-10",
      )}
    >
      {props.children}
    </div>
  );
}

const CELL_HEIGHT = 2.5; // rem

enum Column {
  CATEGORY_TYPE,
  CATEGORY_NAME,
  INPUT_FORMULA,
  BUDGETED_MONTHLY_AVG,
  BUDGETED_TOTAL,
  HISTORICAL_MONTHLY_AVG,
  HISTORICAL_TOTAL,
}

function EditingTableHeaderRow({
  anyGroupsAreExpanded,
  expandOrCollapseAllGroups,
  isEditing,
}: {
  anyGroupsAreExpanded: boolean;
  expandOrCollapseAllGroups: () => void;
  isEditing: boolean;
}) {
  return (
    <div
      className={buildClasses(
        "w-full",
        "h-10",
        "flex",
        "items-center",
        "gap-4",
        "border-b",
        "border-surface-500",
        "sticky",
        "top-0",
        "bg-surface-200",
        "z-10",
      )}
    >
      <Cell
        column={Column.CATEGORY_NAME}
        align="left"
        fontWeight="medium"
        fontSize="md"
      >
        <RyeTooltip
          text={
            anyGroupsAreExpanded
              ? "Collapse all categories"
              : "Expand all categories"
          }
        >
          <div
            className={buildClasses(
              "w-full",
              "flex",
              "items-center",
              "gap-1.5",
              "cursor-pointer",
            )}
            onClick={expandOrCollapseAllGroups}
          >
            {/* As wide as a group's chevron, so "Category" lines up with the
                group names below it. */}
            <span
              className={buildClasses(
                "w-[1.1rem]",
                "shrink-0",
                "flex",
                "justify-center",
              )}
            >
              <RyeIcon
                name={anyGroupsAreExpanded ? "collapse_all" : "expand_all"}
                size="xs"
                className={buildClasses("select-none")}
              />
            </span>
            Category
          </div>
        </RyeTooltip>
      </Cell>
      <Cell
        column={Column.CATEGORY_TYPE}
        align="left"
        fontWeight="medium"
        fontSize="md"
      >
        Type
      </Cell>
      {isEditing && (
        <Cell
          column={Column.INPUT_FORMULA}
          align="left"
          fontWeight="medium"
          fontSize="md"
        >
          Input Formula
        </Cell>
      )}
      <Cell
        column={Column.BUDGETED_MONTHLY_AVG}
        fontWeight="medium"
        fontSize="md"
      >
        Monthly Average
      </Cell>
      <Cell column={Column.BUDGETED_TOTAL} fontWeight="medium" fontSize="md">
        Total
      </Cell>
    </div>
  );
}

function EditingTableGroup({
  groupId,
  group,
  categories,
  groupTotal,
  groupMonthlyAvg,
  isExpanded,
  setGroupIsExpanded,
  budget,
  isEditing,
}: {
  groupId: string;
  group: any;
  categories: Map<string, FirestoreDocCategory>;
  groupTotal: number;
  groupMonthlyAvg: number;
  isExpanded: boolean;
  setGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
  budget: BreadBudget;
  isEditing: boolean;
}) {
  function getHeight(): string {
    if (isExpanded) {
      return `${categories.size * CELL_HEIGHT}rem`;
    }
    return "0px";
  }

  return (
    <div className={buildClasses("w-full")}>
      <EditingTableGroupRow
        groupId={groupId}
        group={group}
        groupTotal={groupTotal}
        groupMonthlyAvg={groupMonthlyAvg}
        isExpanded={isExpanded}
        setGroupIsExpanded={setGroupIsExpanded}
        isEditing={isEditing}
        currency={budget.currency}
      />
      <div
        className={buildClasses(
          "transition-all",
          "duration-300",
          "ease-in-out",
          "overflow-hidden",
        )}
        style={{ height: getHeight() }}
      >
        {Array.from(categories.values()).map((category, index) => {
          const lineItem = budget.line_items.get(category.id);
          const isLastInGroup = index === categories.size - 1;
          return (
            <EditingTableCategoryRow
              key={`category-${category.id}`}
              category={category}
              lineItem={lineItem}
              budget={budget}
              isLastInGroup={isLastInGroup}
              isEditing={isEditing}
            />
          );
        })}
      </div>
    </div>
  );
}

function EditingTableGroupRow({
  groupId,
  group,
  groupTotal,
  groupMonthlyAvg,
  isExpanded,
  setGroupIsExpanded,
  isEditing,
  currency,
}: {
  groupId: string;
  group: any;
  groupTotal: number;
  groupMonthlyAvg: number;
  isExpanded: boolean;
  setGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
  isEditing: boolean;
  currency: string;
}) {
  return (
    <Row marginTop="lg">
      <Cell
        column={Column.CATEGORY_NAME}
        align="left"
        fontWeight="medium"
        fontSize="md"
      >
        <RyeTooltip text={isExpanded ? "Collapse group" : "Expand group"}>
          <div
            className={buildClasses(
              "w-full",
              "flex",
              "items-center",
              "gap-1.5",
              "cursor-pointer",
            )}
            onClick={() => setGroupIsExpanded(groupId, !isExpanded)}
          >
            <RyeIcon
              name="keyboard_arrow_right"
              className={buildClasses(
                { if: isExpanded, then: "rotate-90" },
                "shrink-0",
                "select-none",
                "transition-transform",
              )}
              size="sm"
            />
            <OverflowEllipses fontSize="md">{group.name}</OverflowEllipses>
          </div>
        </RyeTooltip>
      </Cell>
      <Cell column={Column.CATEGORY_TYPE}></Cell>
      {isEditing && <Cell column={Column.INPUT_FORMULA}></Cell>}
      <Cell column={Column.BUDGETED_MONTHLY_AVG} fontWeight="medium">
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          n={groupMonthlyAvg}
          size="md"
          currencyCode={currency}
        />
      </Cell>
      <Cell column={Column.BUDGETED_TOTAL} fontWeight="medium">
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          n={groupTotal}
          size="md"
          currencyCode={currency}
        />
      </Cell>
    </Row>
  );
}

function EditingTableCategoryRow({
  category,
  lineItem,
  budget,
  isLastInGroup = false,
  isEditing,
}: {
  category: FirestoreDocCategory;
  lineItem: BudgetLineItem | undefined;
  budget: BreadBudget;
  isLastInGroup?: boolean;
  isEditing: boolean;
}) {
  const user = useContext(UserContext);

  async function updateFormula(newFormula: string) {
    await firestoreUpdateBudgetLineItem(
      user.uid,
      budget.id,
      category.id,
      newFormula,
    );
  }

  // Calculate monthly average (total amount divided by number of months)
  const numMonths = Math.ceil(
    budget.endDateTimestampSecs.diff(
      budget.beginDateTimestampSecs,
      "month",
      true,
    ),
  );
  const signedAmount = getSignedBudgetLineItemAmount(lineItem, category);
  const monthlyAvg = numMonths > 0 ? signedAmount / numMonths : 0;

  return (
    <Row isLastInGroup={isLastInGroup}>
      <Cell column={Column.CATEGORY_NAME} align="left" indent="pastChevron">
        <CategoryName category={category} />
      </Cell>
      <Cell column={Column.CATEGORY_TYPE} align="left">
        <span className={buildClasses("flex", "items-center", "gap-2.5")}>
          <CategoryTypeIcon type={category.type} />
          {category.type}
        </span>
      </Cell>
      {isEditing && (
        <Cell column={Column.INPUT_FORMULA} align="left">
          <InputFormula
            initialValue={lineItem?.formula || ""}
            onChange={updateFormula}
            categoryType={category.type}
          />
        </Cell>
      )}
      <Cell column={Column.BUDGETED_MONTHLY_AVG}>
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          align="right"
          n={monthlyAvg}
          currencyCode={budget.currency}
        />
      </Cell>
      <Cell column={Column.BUDGETED_TOTAL}>
        {lineItem?.formula_is_valid === false ? (
          <span className={buildClasses("text-danger-500", "text-sm")}>
            Invalid
          </span>
        ) : (
          <DollarAmount
            showPlus
            showMinus
            showDollarSign
            n={signedAmount}
            currencyCode={budget.currency}
          />
        )}
      </Cell>
    </Row>
  );
}

function HistoricalTableHeaderRow() {
  return (
    <div
      className={buildClasses(
        "w-full",
        "h-10",
        "flex",
        "items-center",
        "gap-4",
        "border-b",
        "border-surface-500",
        "sticky",
        "top-0",
        "bg-surface-200",
        "z-10",
      )}
    >
      <Cell
        column={Column.HISTORICAL_MONTHLY_AVG}
        fontWeight="medium"
        fontSize="md"
      >
        Monthly Average
      </Cell>
      <Cell column={Column.HISTORICAL_TOTAL} fontWeight="medium" fontSize="md">
        Total
      </Cell>
    </div>
  );
}

function HistoricalTableGroup({
  groupId,
  group,
  categories,
  groupTotal,
  numMonths,
  isExpanded,
  setGroupIsExpanded,
  historicalSpending,
  currency,
}: {
  groupId: string;
  group: any;
  categories: Map<string, FirestoreDocCategory>;
  groupTotal: number;
  numMonths: number;
  isExpanded: boolean;
  setGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
  historicalSpending: Map<string, number>;
  currency: string;
}) {
  function getHeight(): string {
    if (isExpanded) {
      return `${categories.size * CELL_HEIGHT}rem`;
    }
    return "0px";
  }

  return (
    <div className={buildClasses("w-full")}>
      <HistoricalTableGroupRow
        groupId={groupId}
        group={group}
        groupTotal={groupTotal}
        numMonths={numMonths}
        isExpanded={isExpanded}
        setGroupIsExpanded={setGroupIsExpanded}
        currency={currency}
      />
      <div
        className={buildClasses(
          "transition-all",
          "duration-300",
          "ease-in-out",
          "overflow-hidden",
        )}
        style={{ height: getHeight() }}
      >
        {Array.from(categories.values()).map((category, index) => {
          const historicalAmount = historicalSpending.get(category.id) || 0;
          const isLastInGroup = index === categories.size - 1;
          return (
            <HistoricalTableCategoryRow
              key={`hist-category-${category.id}`}
              historicalAmount={historicalAmount}
              numMonths={numMonths}
              isLastInGroup={isLastInGroup}
              currency={currency}
            />
          );
        })}
      </div>
    </div>
  );
}

function HistoricalTableGroupRow({
  groupId,
  group,
  groupTotal,
  numMonths,
  isExpanded,
  setGroupIsExpanded,
  currency,
}: {
  groupId: string;
  group: any;
  groupTotal: number;
  numMonths: number;
  isExpanded: boolean;
  setGroupIsExpanded: (groupId: string, isExpanded: boolean) => void;
  currency: string;
}) {
  const monthlyAvg = numMonths > 0 ? groupTotal / numMonths : 0;

  return (
    <Row marginTop="lg">
      <Cell column={Column.HISTORICAL_MONTHLY_AVG} fontWeight="medium">
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          n={monthlyAvg}
          size="md"
          currencyCode={currency}
        />
      </Cell>
      <Cell column={Column.HISTORICAL_TOTAL} fontWeight="medium">
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          n={groupTotal}
          size="md"
          currencyCode={currency}
        />
      </Cell>
    </Row>
  );
}

function HistoricalTableCategoryRow({
  historicalAmount,
  numMonths,
  isLastInGroup = false,
  currency,
}: {
  historicalAmount: number;
  numMonths: number;
  isLastInGroup?: boolean;
  currency: string;
}) {
  const monthlyAvg = numMonths > 0 ? historicalAmount / numMonths : 0;

  return (
    <Row isLastInGroup={isLastInGroup}>
      <Cell column={Column.HISTORICAL_MONTHLY_AVG}>
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          n={monthlyAvg}
          currencyCode={currency}
        />
      </Cell>
      <Cell column={Column.HISTORICAL_TOTAL}>
        <DollarAmount
          showPlus
          showMinus
          showDollarSign
          n={historicalAmount}
          currencyCode={currency}
        />
      </Cell>
    </Row>
  );
}

function Row(props: {
  children?: React.ReactNode;
  marginTop?: "sm" | "md" | "lg";
  isLastInGroup?: boolean;
}) {
  return (
    <div
      className={buildClasses(
        "w-full",
        "h-10",
        "flex",
        "items-center",
        "gap-4",
        {
          if: !props.isLastInGroup,
          then: buildClasses("border-b", "border-surface-500"),
        },
        {
          switch: props.marginTop,
          cases: new Map([
            ["sm", "mt-2"],
            ["md", "mt-4"],
            ["lg", "mt-6"],
          ]),
        },
      )}
    >
      {props.children}
    </div>
  );
}

function Cell({
  column,
  align = "right",
  fontWeight = "normal",
  fontSize = "sm",
  textColor = "normal",
  indent,
  children,
}: {
  column: Column;
  align?: "left" | "right" | "center";
  fontWeight?: "normal" | "medium";
  fontSize?: "sm" | "md";
  textColor?: "normal" | "light";
  indent?: "pastChevron";
  children?: React.ReactNode;
}) {
  return (
    <div
      className={buildClasses(
        {
          if: column === Column.CATEGORY_TYPE,
          then: "w-24",
          elseIfs: [
            { elseIf: column === Column.CATEGORY_NAME, then: "w-24" },
            { elseIf: column === Column.INPUT_FORMULA, then: "w-24" },
            { elseIf: column === Column.BUDGETED_MONTHLY_AVG, then: "w-24" },
            { elseIf: column === Column.BUDGETED_TOTAL, then: "w-24" },
            { elseIf: column === Column.HISTORICAL_MONTHLY_AVG, then: "w-24" },
            { elseIf: column === Column.HISTORICAL_TOTAL, then: "w-24" },
          ],
        },
        {
          switch: align,
          cases: new Map([
            ["left", "justify-start"],
            ["right", "justify-end"],
            ["center", "justify-center"],
          ]),
        },
        {
          switch: fontWeight,
          cases: new Map([
            ["normal", "font-normal"],
            ["medium", "font-medium"],
          ]),
        },
        {
          switch: fontSize,
          cases: new Map([
            ["sm", "text-sm"],
            ["md", "text-md"],
          ]),
        },
        {
          switch: textColor,
          cases: new Map([
            ["normal", "text-on-surface-900"],
            ["light", "text-on-surface-500"],
          ]),
        },
        {
          switch: indent,
          // Past a group's chevron and its gap, so categories line up
          // under their group's name.
          cases: new Map([["pastChevron", "pl-6"]]),
        },
        // Lets cells shrink below their contents, which truncate, when the
        // historical panel narrows the table.
        "min-w-0",
        "flex",
        "items-center",
        "grow",
      )}
    >
      {children}
    </div>
  );
}

function CategoryName({ category }: { category: any }) {
  return (
    <div className={buildClasses("flex", "gap-2", "min-w-0")}>
      {category.emoji}{" "}
      <OverflowEllipses fontSize="sm">{category.name}</OverflowEllipses>
    </div>
  );
}

function InputFormula({
  initialValue,
  onChange,
  categoryType,
}: {
  initialValue: string;
  onChange: (value: string) => void;
  categoryType: string;
}) {
  // Determine the sign based on category type
  const getSignForCategoryType = (type: string) => {
    switch (type) {
      case "Income":
      case "Savings":
        return "+";
      case "Expense":
        return "-";
      default:
        return "";
    }
  };

  const sign = getSignForCategoryType(categoryType);

  return (
    <div className={buildClasses("flex", "items-center", "gap-1")}>
      {sign && (
        <span
          className={buildClasses(
            "text-sm",
            "text-on-surface-700",
            "font-medium",
          )}
        >
          {sign}
        </span>
      )}
      <RyeInput
        startingText={initialValue}
        size="sm"
        width="full"
        onChange={onChange}
      />
    </div>
  );
}

function OverflowEllipses(props: {
  children: React.ReactNode;
  fontSize?: "sm" | "md";
}) {
  return (
    <span
      className={buildClasses(
        "whitespace-nowrap",
        "overflow-hidden",
        "overflow-ellipsis",
        {
          switch: props.fontSize || "sm",
          cases: new Map([
            ["sm", "text-sm"],
            ["lg", "text-md"],
          ]),
        },
      )}
    >
      {props.children}
    </span>
  );
}
